import { DomUtil } from '../utility/DomUtil';
import { EventHandleSpec, NormalizedEvent } from '../EventUtils';
import { Gesture, GestureEventInfo } from './Gesture';
import { GestureType, PseudoEventName } from '../../gen-src/preslayer/Enums';
import { Logger } from '../utility/VizclientUtils';
import { MiscUtil } from '../utility/MiscUtil';
import { TypeUtil } from '../utility/TypeUtil';

class Hover extends Gesture {
  public static readonly HoverTime: number = 150;
  public static readonly FastHoverTime: number = 120;

  private startX: number = 0;
  private startY: number = 0;
  private possible: boolean = true;
  private isSuppressed: boolean;
  private hoverTimeout?: number;

  private hoverTime: number;

  /**
   * Initializes a new instance of the Hover class
   * @param {EventHandleSpec} config - GestureEventHandleSpec object
   */
  public constructor(config: EventHandleSpec) {
    super(config, GestureType.Hover, /*hasSlop*/false);
    this.hoverTime = (MiscUtil.IsNullOrUndefined(config.HoverDelay)) ? Hover.HoverTime : config.HoverDelay;
  }

  public Start(e: NormalizedEvent): void {
    Logger.Debug('Start');
    this.Cancel();
    this.possible = false;
  }

  public Move(e: NormalizedEvent): void {
    Logger.Debug(`Move: ${e}`);
    let moveEvent = e.touches[0];
    if (!this.possible) {
      return; // mouse down => not possible
    }

    if (!this.PastSlop(e)) {
      return; // either before or after hover fires, do nothing within slop
    }

    let param = this.GetParam(e);
    if (!MiscUtil.IsNullOrUndefined(this.hoverTimeout)) {
      this.Cancel();
    }

    this.Fire(PseudoEventName.Moving, e, param);

    this.startX = moveEvent.pageX;
    this.startY = moveEvent.pageY;
    this.hoverTimeout = window.setTimeout(() => {
      Logger.Debug('Hover');
      if (!this.isSuppressed) {
        this.Fire(PseudoEventName.Hover, e, param);
      }
      this.ClearTimeout();
    },
      // tslint:disable-next-line:align
      this.hoverTime);
  }

  public End(e: NormalizedEvent): void {
    Logger.Debug('end');
    if (e.touches.length === 0) {
      this.possible = true;
    }
  }

  public Out(e: NormalizedEvent): void {
    // http://www.quirksmode.org/js/events_mouse.html#relatedtarget (dojo normalizes IE for us)
    // in case of mouseout, relatedTarget is the element the mouse is going to.
    // We stop the hover if we are going to an element that is neither the originator of the hover,
    // nor a child of the originator.
    let relatedTarget: HTMLElement = TypeUtil.GetField(e.originalEvent, 'relatedTarget');
    if (this.Config.DomElement !== relatedTarget && !DomUtil.IsAncestorOf(this.Config.DomElement, relatedTarget)) {
      Logger.Debug('Out.');
      this.Cancel();
      this.Fire(PseudoEventName.MouseLeave, e, this.GetParam(e));
    }
  }

  public suppressHover(): void {
    if (!this.isSuppressed) {
      Logger.Debug('SuppressHover.');
    }
    this.isSuppressed = true;
  }
  public unsuppressHover(): void {
    if (this.isSuppressed) {
      Logger.Debug('UnsuppressHover.');
    }
    // TODO - old code had a "trigger hover if we haven't moved from the suppress point"
    this.isSuppressed = false;

    // Depending on where/when mouse events occured our state may no longer be valid.
    // Reset that state when this method is called, assuming that it will be called
    // at appropriate times.
    this.possible = true;
  }

  public Cancel(): void {
    this.ClearTimeout();
    this.startX = 0;
    this.startY = 0;
    this.possible = true;
  }

  private ClearTimeout(): void {
    this.hoverTimeout = MiscUtil.ClearTimeout(this.hoverTimeout);
  }

  private PastSlop(e: NormalizedEvent): boolean {
    let touch: Touch = e.touches[0];
    let diffX: number = Math.abs(touch.pageX - this.startX);
    let diffY: number = Math.abs(touch.pageY - this.startY);
    return (diffX + diffY) > this.ZoomAdjustedSlop;
  }

  private GetParam(e: NormalizedEvent): GestureEventInfo {
    let param: GestureEventInfo = new GestureEventInfo();
    param.pageX = e.changedTouches[0].pageX;
    param.pageY = e.changedTouches[0].pageY;
    param.clientX = e.changedTouches[0].clientX;
    param.clientY = e.changedTouches[0].clientY;
    return param;
  }
}

export { Hover };
